import React, { useEffect, useState } from 'react';
import {
  Grid,
  WithStyles,
  Divider,
  Typography,
  Switch,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  FormHelperText,
} from '@material-ui/core';
import styles from './styles';
import { StaffHoursType } from '../../redux/types/properties';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment, { Moment } from 'moment';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { validateExpiresOnAgainstWindow, validateSingleExpiresOn } from './validation';
import { usePermissions } from '../../hooks/usePermissions';

type Props = {
  handleStaffHours: (staffHours: StaffHoursType[] | null, lockInterval: string | null) => void;
  staffHours: StaffHoursType[];
  lockInterval?: string | null;
} & WithStyles<typeof styles>;

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const defaultWeeklyStaffHours = weekDays.map((weekDay, index) => ({
  dayOfWeek: index,
  isStaffAvailable: true,
  staffHoursStart: '12:00 PM',
  staffHoursEnd: '3:00 PM',
  expires_on: null,
}));

const lockIntervalOption = ['1 min', '3 min', '5 min', '10 min', '15 min', '20 min', '30 min'];

const StaffHours = (props: Props) => {
  const {
    classes, handleStaffHours, staffHours, lockInterval,
  } = props;

  const [weeklyStaffHours, setWeeklyStaffHours] = useState<
    StaffHoursType[] | null
  >(defaultWeeklyStaffHours);
  const [isEnableStaffHours, setIsEnableStaffHours] = useState<boolean>(false);
  const [staffLockInterval, setStaffLockInterval] = useState<string | null>(null);
  const [expiresOnErrors, setExpiresOnErrors] = useState<Record<number, string | null>>({});

  const { canEdit } = usePermissions();

  useEffect(() => {
    handleStaffHours(isEnableStaffHours ? weeklyStaffHours : null, staffLockInterval);
  }, [handleStaffHours, weeklyStaffHours, isEnableStaffHours, staffLockInterval]);

  useEffect(() => {
    if (!staffHours?.length) {
      setIsEnableStaffHours(false);
      return;
    }
    setWeeklyStaffHours(staffHours);
    setIsEnableStaffHours(true);
    setStaffLockInterval(lockInterval ?? '10 min');
  }, [staffHours, lockInterval]);

  const generateTimes = (staffStartTime?: string | null): JSX.Element[] => {
    const result: JSX.Element[] = [];
    const parseTime = (timeStr: string): Date => {
      const [time, period] = timeStr.split(' ');
      const [hoursStr, minutesStr] = time.split(':');
      let hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;

      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    };

    const startLimit = new Date();
    startLimit.setHours(7, 0, 0, 0); // 07:00 AM

    const endLimit = new Date();
    endLimit.setHours(20, 30, 0, 0); // 08:30 PM

    let startTime: Date;

    if (staffStartTime) {
      startTime = parseTime(staffStartTime);
      startTime = new Date(startTime.getTime() + 30 * 60 * 1000); // shift +30 min
    } else {
      startTime = new Date(startLimit);
    }

    const formatTime = (date: Date): string => {
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes < 10 ? '00' : minutes} ${period}`;
    };

    while (startTime <= endLimit) {
      result.push(
        <MenuItem key={startTime.toString()} value={formatTime(startTime)}>
          {formatTime(startTime)}
        </MenuItem>,
      );
      startTime.setMinutes(startTime.getMinutes() + 30);
    }

    return result;
  };

  const handleChange = (
    day: number,
    event:
      React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const { target } = event;
    const name = target.name!;
    const value = (target as HTMLInputElement).type === 'checkbox'
      ? (target as HTMLInputElement).checked
      : target.value;

    setWeeklyStaffHours((prevHours) => (
      prevHours
        ? prevHours.map((item) => (item.dayOfWeek === day ? { ...item, [name]: value } : item))
        : null
    ));
  };

  const handleIsEnableStaffHours = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsEnableStaffHours(e.target.checked);
  };

  const allowOnlySelectedWeekday = (weekday: number) => (
    day: MaterialUiPickersDate
  ) => {
    if (!day) return false;
    const m = moment.isMoment(day) ? day : moment(day as Date);
    return m.day() !== weekday;
  };


  const handleExpiresOnChange = (dayOfWeek: number) => (
    val: MaterialUiPickersDate
  ) => {
    // Normalize MaterialUiPickersDate -> Moment | null
    const toMoment = (val: MaterialUiPickersDate): Moment | null => {
      if (!val) return null;
      return moment.isMoment(val) ? (val as Moment) : moment(val as Date);
    };

    // Patch a single day in weeklyStaffHours
    const updateDay = (dayOfWeek: number, patch: Partial<StaffHoursType>) => {
      setWeeklyStaffHours(prev =>
        prev
          ? prev.map(row =>
              row.dayOfWeek === dayOfWeek ? { ...row, ...patch } : row
            )
          : prev
      );
    };

    const m = toMoment(val);

    // Clearing? wipe value and error, exit.
    if (!m || !m.isValid()) {
      setExpiresOnErrors(prev => ({ ...prev, [dayOfWeek]: null }));
      updateDay(dayOfWeek, { expires_on: null });
      return;
    }

    const iso = m.toDate().toISOString();

    const base = validateSingleExpiresOn(iso);

    if (!base.ok) {
      setExpiresOnErrors(prev => ({ ...prev, [dayOfWeek]: base.msg }));
      return;
    }

    const currentRow = weeklyStaffHours?.find(r => r.dayOfWeek === dayOfWeek);

    if (currentRow && currentRow.staffHoursStart && currentRow.staffHoursEnd) {
      const { ok, msg } = validateExpiresOnAgainstWindow(
        iso,
        currentRow.dayOfWeek,
        currentRow.staffHoursStart,
        currentRow.staffHoursEnd
      );

      if (!ok) {
        setExpiresOnErrors(prev => ({ ...prev, [dayOfWeek]: msg }));
        return;
      }
    }

    setExpiresOnErrors(prev => ({ ...prev, [dayOfWeek]: null }));
    updateDay(dayOfWeek, { expires_on: iso });
  };


  return (
    <>
      <Grid
        container
        direction="column"
      >
        <FormControl className={classes.formControl}>
          <FormControlLabel
            disabled={!canEdit}
            control={(
              <Checkbox
                checked={isEnableStaffHours}
                onChange={handleIsEnableStaffHours}
              />
            )}
            label={(
              <>
                <span>Enable Staff Hours</span>
                <span className={classes.beta}>Beta</span>
              </>
            )}
          />
          <FormHelperText>
            Turns on auto-locking outside of set staffed hours. This feature allows the staff to not be bothered by NterNow AutoLocking the door.
          </FormHelperText>
        </FormControl>
        <Box className={`${classes.lockBox} ${!isEnableStaffHours && classes.disabled}`}>
          <p className={classes.lockTitle}>
            Auto Lock Interval
          </p>
          <Select
            className={classes.select}
            variant="outlined"
            value={staffLockInterval}
            disabled={!canEdit}
            onChange={(
              event: React.ChangeEvent<{ value: unknown }>,
            ) => {
              const { value } = event.target;

              setStaffLockInterval(value as string);
            }}
          >
            {lockIntervalOption.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Divider style={{ marginBottom: '40px', marginTop: '40px' }} />
        {weeklyStaffHours?.length && weeklyStaffHours.map((item) => (
          <Grid
            container
            alignItems="center"
            spacing={2}
            key={item.dayOfWeek}
            className={`${!isEnableStaffHours && classes.disabled}`}
          >
            <Grid item className={classes.dayBox}>
              <Typography className={classes.day}>
                {weekDays[item.dayOfWeek]}
              </Typography>
            </Grid>
            <Grid item className={classes.switchBox}>
              <Switch
                checked={item.isStaffAvailable}
                onChange={(e) => handleChange(item.dayOfWeek, e)}
                name="isStaffAvailable"
                disabled={!canEdit}
              />
            </Grid>
            {item.isStaffAvailable ? (
              <Grid container spacing={6} xs>
                <Grid item>
                  <Typography className={classes.staffWindow}>
                    Staff Window
                  </Typography>
                  <Grid item>
                    <Select
                      name="staffHoursStart"
                      variant="outlined"
                      value={item.staffHoursStart ?? ''}
                      onChange={(e) => handleChange(item.dayOfWeek, e)}
                      className={classes.select}
                      disabled={!canEdit}
                    >
                      {generateTimes()}
                    </Select>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.interval}>
                      To
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Select
                      name="staffHoursEnd"
                      variant="outlined"
                      value={item.staffHoursEnd ?? ''}
                      onChange={(e) => handleChange(item.dayOfWeek, e)}
                      className={classes.select}
                      disabled={!canEdit}
                    >
                      {generateTimes(item.staffHoursStart)}
                    </Select>
                  </Grid>
                </Grid>
                {/* Per-day Expires On */}
                <Grid item>
                  <Typography className={classes.expiresOnWindow}>
                    Expires On
                  </Typography>
                  <MuiPickersUtilsProvider utils={MomentUtils} locale="en">
                    <>
                      <DateTimePicker
                        label="Expires On"
                        inputVariant="outlined"
                        disablePast
                        value={item.expires_on ? moment(item.expires_on) : null}
                        onChange={handleExpiresOnChange(item.dayOfWeek)}
                        className={classes.expiresField}
                        clearable
                        FormHelperTextProps={{ classes: { root: classes.expiresHelper } }}
                        helperText={expiresOnErrors[item.dayOfWeek]}
                        error={Boolean(expiresOnErrors[item.dayOfWeek])}
                        shouldDisableDate={allowOnlySelectedWeekday(item.dayOfWeek)}
                        disabled={!canEdit}
                      />
                    </>
                  </MuiPickersUtilsProvider>
                </Grid>
              </Grid>
            ) : (
              <Grid item>
                <Typography>No Staff, Auto Lock Enabled</Typography>
              </Grid>
            )}
            <Divider className={classes.divider} />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default StaffHours;
